import React, { useEffect, useMemo, useRef, useState } from "react";
import { Routes, Route, Navigate, NavLink, useNavigate, useLocation } from "react-router-dom";
import { collection, doc, getDoc, getDocs, limit, query, where } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { getAuthClient, functions } from "../firebaseConfig";
import { httpsCallable } from "firebase/functions";

import OverviewTab from "./OverviewTab";
import StaffDashboardTab from "./StaffDashboardTab";
import AdminReportsPage from "../pages/AdminReportsPage";
import ManagersTab from "./ManagersTab";
import StaffTab from "./StaffTab";
import ActiveStaffTab from "./ActiveStaffTab";
import ScheduleIntelligenceTab from "./ScheduleIntelligenceTab.jsx";
import LaborTab from "./LaborTab";
import PayrollTab from "./PayrollTab";
import SettingsTab from "./SettingsTab";
import BrandingTab from "./BrandingTab";
import MyTicketsTab from "./MyTicketsTab.jsx";
import BillingTab from "./BillingTab.jsx";
import ProfilePage from "../pages/ProfilePage";
import ActivityLogPage from "../pages/ActivityLogPage";

import { useAuth } from "../auth/AuthProvider";
import { normalizeClaims } from "../auth/landing";
import { db } from "../firebaseConfig";
import useOrgBranding from "./useOrgBranding";
import { clearActiveApp, hasMorpheusAccess, setActiveApp } from "../auth/appAccess";

export default function AdminPanel() {
  const { user, claims, loading } = useAuth();
  const [orgName, setOrgName] = useState("");
  const [memberName, setMemberName] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpTab, setHelpTab] = useState("");
  const [helpTopic, setHelpTopic] = useState("");
  const [helpMessage, setHelpMessage] = useState("");
  const [helpSendState, setHelpSendState] = useState({ status: "idle", message: "" });
  const [helpTabMenuOpen, setHelpTabMenuOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [tabPerms, setTabPerms] = useState(null);
  const [managementAccess, setManagementAccess] = useState({ loading: true, matched: false, neoAdminAccess: false, personId: "", roleId: "", roleRank: null, managePeople: false, manageRolesAccess: false, capabilities: [], siteIds: [], allSites: false, viewAttendancePhotos: false });
  const [ticketUnreadCount, setTicketUnreadCount] = useState(0);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const canSendHelp = Boolean(helpTab.trim()) && Boolean(helpTopic.trim()) && Boolean(helpMessage.trim());
  const helpTabRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const payrollWorkspace = /^\/cashup\/payroll(?:\/|$)/.test(location.pathname);

  const helpTabOptions = useMemo(
    () => [
      { value: "", label: "Select a tab..." },
      { value: "Overview", label: "Overview" },
      { value: "Staff Dashboard", label: "Staff Dashboard" },
      { value: "Reports", label: "Reports" },
      { value: "Management", label: "Management" },
      { value: "Staff", label: "Staff" },
      { value: "Active Staff", label: "Active Staff" },
      { value: "Schedule Intelligence", label: "Schedule Intelligence" },
      { value: "Labor", label: "Labor" },
      { value: "Payroll", label: "Payroll" },
      { value: "Settings", label: "Settings" },
      { value: "Billing", label: "Billing" },
      { value: "My Tickets", label: "My Tickets" },
      { value: "Branding", label: "Branding" },
      { value: "Activity Log", label: "Activity Log" },
      { value: "Other", label: "Other" },
    ],
    []
  );

  const helpTabLabel = (helpTabOptions.find((o) => o.value === helpTab) || helpTabOptions[0]).label;

  const norm = normalizeClaims(claims || {});
  const orgId = norm.orgId || null;

  useEffect(() => {
    if (!user || !orgId) {
      setMemberName("");
      return undefined;
    }

    let cancelled = false;
    const loadMemberName = async () => {
      try {
        const snap = await getDoc(doc(db, "orgs", orgId, "members", user.uid));
        const directName = snap.exists() ? snap.data()?.displayName || "" : "";
        if (directName) {
          if (!cancelled) setMemberName(directName);
          return;
        }

        const email = String(user?.email || "").trim().toLowerCase();
        if (!email) {
          if (!cancelled) setMemberName("");
          return;
        }
        const byEmail = await getDocs(
          query(collection(db, "orgs", orgId, "members"), where("email", "==", email), limit(1))
        );
        const fallbackName = byEmail.empty ? "" : byEmail.docs[0].data()?.displayName || "";
        if (!cancelled) setMemberName(fallbackName || "");
      } catch {
        if (!cancelled) setMemberName("");
      }
    };

    // Admin identity is reference data, not operational state. Read it once when
    // the Admin shell opens instead of keeping a permanent member listener alive.
    loadMemberName();
    return () => { cancelled = true; };
  }, [user, orgId]);

  const { logo } = useOrgBranding(orgId);
  const displayName = memberName || user?.displayName || user?.email || "User";
  const canSwitchToMorpheus = hasMorpheusAccess(norm);

  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  function refreshNeo() {
    if (isRefreshing) return;
    setIsRefreshing(true);

    // This is intentionally user-triggered only: no polling, timers, or extra
    // background reads. Reload the current Neo URL so the iPad PWA refreshes
    // in place instead of sending the admin back through the homepage.
    window.setTimeout(() => {
      window.location.reload();
    }, 80);
  }

  useEffect(() => {
    if (!helpTabMenuOpen) return;
    function onDocDown(e) {
      if (!helpTabRef.current) return;
      if (!helpTabRef.current.contains(e.target)) setHelpTabMenuOpen(false);
    }
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [helpTabMenuOpen]);

  useEffect(() => {
    if (!helpOpen) setHelpTabMenuOpen(false);
  }, [helpOpen]);

  const SUPPORT_EMAIL = "admin@matrixos.co.za";

  async function openMailToSupport() {
    const topic = (helpTopic || helpTab || "Help").trim() || "Help";
    const bodyLines = [
      "Hello MatrixOS Support,",
      "",
      `I need help with: ${topic}`,
      "",
      helpMessage.trim() ? `Details:\n${helpMessage.trim()}` : "Details: (please describe what happened)",
      "",
      "---",
      `Org: ${orgName || orgId || "-"}`,
      `User: ${user?.displayName || user?.email || "-"}`,
      `Page: ${window.location.pathname}`,
      `Time: ${new Date().toLocaleString()}`,
    ];

    const subject = `Neo Admin Panel Support - ${topic}`;
    const body = bodyLines.join("\n");
    const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    setHelpSendState({ status: "sending", message: "Opening your email app…" });

    let copied = false;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(`To: ${SUPPORT_EMAIL}\nSubject: ${subject}\n\n${body}`);
        copied = true;
      }
    } catch {}

    try {
      const w = window.open(mailto, "_blank");
      if (!w) window.location.href = mailto;
      setHelpSendState({
        status: "success",
        message: copied
          ? "Email details copied to clipboard. Your email app should open now."
          : "Your email app should open now. If it doesn't, email admin@matrixos.co.za and describe the issue.",
      });
    } catch {
      setHelpSendState({
        status: "error",
        message: copied
          ? "Could not open your email app, but we copied the email details to clipboard."
          : "Could not open your email app. Please email admin@matrixos.co.za and include the details.",
      });
    }
  }

  function closeAllOverlays() {
    setMenuOpen(false);
    setAboutOpen(false);
    setHelpOpen(false);
  }

  // Development mode: idle auto-logout temporarily disabled.

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!orgId) return setOrgName("");
      try {
        const snap = await getDoc(doc(db, "orgs", orgId));
        if (!cancelled) setOrgName(snap.data()?.name || "");
      } catch {
        if (!cancelled) setOrgName("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!orgId || !user?.uid) return;
      try {
        const snap = await getDoc(doc(db, "orgs", orgId, "cashup_users", user.uid));
        if (cancelled) return;
        const tabs = snap.exists() ? snap.data()?.tabs || snap.data()?.tabsAllowed || null : null;
        setTabPerms(Array.isArray(tabs) ? tabs : null);
      } catch {
        if (!cancelled) setTabPerms(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, user?.uid]);

  // Phase 8 — resolve Neo Management authority server-side. The callable also
  // maintains the UID-keyed neoAccessProfiles projection used by Firestore rules,
  // so stale legacy member/userRoles metadata cannot widen current Management
  // authority. One on-demand call occurs when the Admin shell opens; no polling.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!orgId || !user?.uid) {
        if (!cancelled) setManagementAccess({ loading: false, matched: false, neoAdminAccess: false, personId: "", roleId: "", roleRank: null, managePeople: false, manageRolesAccess: false, capabilities: [], siteIds: [], allSites: false, viewAttendancePhotos: false });
        return;
      }
      try {
        const call = httpsCallable(functions, "getNeoAccessContext");
        const response = await call({ orgId });
        const data = response?.data || {};
        if (cancelled) return;
        setManagementAccess({
          loading: false,
          matched: data.matched === true,
          neoAdminAccess: data.neoAdminAccess === true,
          personId: String(data.personId || ""),
          roleId: String(data.roleId || ""),
          roleRank: Number.isFinite(Number(data.roleRank)) ? Number(data.roleRank) : null,
          managePeople: data.managePeople === true,
          manageRolesAccess: data.manageRolesAccess === true,
          viewAttendancePhotos: data.viewAttendancePhotos === true,
          capabilities: Array.isArray(data.capabilities) ? data.capabilities : [],
          siteIds: Array.isArray(data.siteIds) ? data.siteIds.filter(Boolean) : [],
          allSites: data.allSites === true,
        });
      } catch (error) {
        console.error("[Neo Access] Could not resolve Management authority", error);
        if (!cancelled) setManagementAccess({ loading: false, matched: false, neoAdminAccess: false, personId: "", roleId: "", roleRank: null, managePeople: false, manageRolesAccess: false, capabilities: [], siteIds: [], allSites: false, viewAttendancePhotos: false });
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, user?.uid]);

  const isNeoTicket = (ticket = {}) => {
    const source = String(ticket.sourceApp || ticket.app || ticket.platform || "").toLowerCase();
    const number = String(ticket.ticketNumber || ticket.ref || "").toLowerCase();
    if (source) return ["neo_admin", "neo", "cashup", "cashup_admin", "neo_operations"].includes(source);
    return number.startsWith("neo-");
  };

  useEffect(() => {
    if (!orgId) {
      setTicketUnreadCount(0);
      return undefined;
    }

    let cancelled = false;
    const loadTicketUnreadCount = async () => {
      try {
        const snap = await getDocs(collection(db, "orgs", orgId, "supportTickets"));
        let count = 0;
        snap.forEach((d) => {
          const data = d.data() || {};
          if (!isNeoTicket(data)) return;
          const lastReplyBy = String(data.lastReplyBy || "").toLowerCase();
          const lastReplyAt = data.lastReplyAt?.toDate ? data.lastReplyAt.toDate() : null;
          const clientViewedAt = data.clientLastViewedAt?.toDate ? data.clientLastViewedAt.toDate() : null;
          if (lastReplyBy === "support" && lastReplyAt && (!clientViewedAt || lastReplyAt > clientViewedAt)) count += 1;
        });
        if (!cancelled) setTicketUnreadCount(count);
      } catch {
        if (!cancelled) setTicketUnreadCount(0);
      }
    };

    // Support is not part of Neo's operational live core. A permanent listener on
    // the entire ticket collection made every Admin page pay for support updates.
    // Load on entry and refresh when the browser/app regains focus instead.
    loadTicketUnreadCount();
    const onFocus = () => loadTicketUnreadCount();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
    };
  }, [orgId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white" style={{ backgroundColor: "var(--brand-bg)" }}>
        Checking access…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (managementAccess.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white" style={{ backgroundColor: "var(--brand-bg)" }}>
        Loading Neo access…
      </div>
    );
  }

  const hasOrg = !!norm.orgId;
  const roles = Array.isArray(norm.role) ? norm.role : norm.role ? [norm.role] : [];
  const normalizedApps = (norm.apps || []).map((app) => String(app || "").toLowerCase());
  const normalizedDefaultApp = String(norm.defaultApp || "").toLowerCase();
  const canUseCashup =
    normalizedApps.some((app) => ["neo", "cashup", "neo_admin", "admin", "cash-up"].includes(app)) ||
    roles.includes("cashup") ||
    roles.includes("neo") ||
    ["neo", "cashup", "neo_admin", "admin", "cash-up"].includes(normalizedDefaultApp) ||
    roles.some((r) => ["owner", "admin", "viewer", "cashup_owner", "cashup_admin", "cashup_viewer"].includes(String(r).toLowerCase()));

  if (!hasOrg || !canUseCashup) return <Navigate to="/no-access" replace />;

  const doLogout = async () => {
    try {
      try {
        clearActiveApp();
        window.localStorage.removeItem("im.loginApp");
      } catch (_) {}
      const auth = getAuthClient();
      await signOut(auth);
    } finally {
      window.location.replace("/login");
    }
  };

  const switchToMorpheus = () => {
    if (!canSwitchToMorpheus) return;
    closeAllOverlays();
    try {
      setActiveApp("morpheus");
      window.localStorage.setItem("im.loginApp", "mc");
    } catch (_) {}
    window.location.assign("/morpheus-count/workspace");
  };

  const isOwner = norm.orgAdmin === true || roles.includes("owner") || roles.includes("cashup_owner");
  // Once an authenticated user has a canonical Management Person, its Neo Admin
  // Access switch is authoritative. Never widen an inactive Management user back
  // to legacy Viewer/Admin defaults.
  if (managementAccess.matched && managementAccess.neoAdminAccess !== true && !isOwner) {
    return <Navigate to="/no-access" replace />;
  }
  const isViewer = roles.includes("viewer") || roles.includes("cashup_viewer");
  const isAdmin = roles.includes("admin") || roles.includes("cashup_admin");

  const DEFAULT_TABS_OWNER = [
    "overview",
    "staff",
    "reports",
    "managers",
    "staff-list",
    "active",
    "schedule",
    "labor",
    "payroll",
    "settings",
    "billing",
    "my-tickets",
    "branding",
    "activity",
  ];
  const DEFAULT_TABS_ADMIN = ["overview", "staff", "reports", "staff-list", "active", "schedule", "labor", "billing", "my-tickets", "activity"];
  const DEFAULT_TABS_VIEWER = ["overview", "staff", "reports", "my-tickets"];

  const roleCapabilityTabs = Array.isArray(managementAccess.capabilities) ? managementAccess.capabilities : [];
  // Phase 4 — Owner/legacy admins retain organisation scope. A matched Management Person
  // receives only the sites assigned on their canonical Management record.
  const neoSiteScope = isOwner || !managementAccess.matched
    ? { allSites: true, siteIds: [] }
    : { allSites: managementAccess.allSites === true, siteIds: managementAccess.siteIds || [] };
  const hasManagementRoleAccess = managementAccess.matched && managementAccess.neoAdminAccess && managementAccess.roleId;

  // Owner remains root authority. For a Management Person with Neo Admin Access ON,
  // the organisation-defined role is authoritative for navigation and routes.
  // Legacy cashup_users tab permissions remain a compatibility fallback only for
  // existing Admin/Viewer identities that have not yet migrated to Management.
  const rawAllowedTabs = isOwner
    ? DEFAULT_TABS_OWNER
    : hasManagementRoleAccess
      ? roleCapabilityTabs
      : tabPerms
        ? tabPerms
        : isViewer
          ? DEFAULT_TABS_VIEWER
          : isAdmin
            ? DEFAULT_TABS_ADMIN
            : DEFAULT_TABS_ADMIN;

  const allowedTabs = Array.from(new Set(rawAllowedTabs || []));
  const can = (tab) => allowedTabs.includes(tab);
  const firstTab = allowedTabs[0] || "overview";

  const baseLink = "block px-4 py-2 rounded-lg transition text-white focus:outline-none focus:ring-2 focus:ring-offset-2";
  const linkStyle = ({ isActive }) => ({
    backgroundColor: isActive ? "var(--brand-accent)" : "var(--brand-surface)",
    color: "var(--brand-text)",
  });

  return (
    <div className={`neo-admin-root flex min-h-screen text-white ${payrollWorkspace ? "neo-payroll-workspace" : ""}`} style={{ backgroundColor: "var(--brand-bg)" }}>
      <style>{`
        /* Payroll owns the full content surface; Neo Admin navigation stays on other routes. */
        .neo-payroll-workspace .neo-admin-main { width: 100%; min-width: 0; }
        .neo-payroll-workspace .neo-admin-content { width: 100%; min-width: 0; padding: 0 !important; overflow-x: hidden; }
        .neoHelpSelect option { background-color: var(--surface); color: var(--brand-text); }
        .neo-admin-content table { border-collapse: collapse; }
        .neo-admin-content .neo-mobile-scroll-hint { display: none; }

        @media (max-width: 820px) {
          html, body, #root { min-width: 0 !important; width: 100% !important; overflow-x: hidden !important; }
          .neo-admin-root {
            display: flex !important;
            flex-direction: column !important;
            min-height: 100svh !important;
            width: 100% !important;
            overflow-x: hidden !important;
          }
          .neo-admin-sidebar {
            width: 100% !important;
            min-width: 0 !important;
            position: sticky !important;
            top: 0 !important;
            z-index: 60 !important;
            padding: 1rem !important;
            border-bottom: 1px solid rgba(255,255,255,.10) !important;
            box-shadow: 0 18px 48px rgba(0,0,0,.28) !important;
          }
          .neo-admin-sidebar nav {
            display: flex !important;
            gap: .55rem !important;
            overflow-x: auto !important;
            overflow-y: hidden !important;
            padding-bottom: .45rem !important;
            scroll-snap-type: x proximity !important;
            -webkit-overflow-scrolling: touch !important;
          }
          .neo-admin-sidebar nav a {
            flex: 0 0 auto !important;
            white-space: nowrap !important;
            border-radius: 999px !important;
            padding: .72rem 1rem !important;
            scroll-snap-align: center !important;
          }
          .neo-admin-sidebar h2 { margin-bottom: .8rem !important; }
          .neo-admin-sidebar > div:first-child > div.mb-1 { font-size: 1.35rem !important; line-height: 1.1 !important; }
          .neo-admin-main { width: 100% !important; min-width: 0 !important; }
          .neo-admin-topbar { position: sticky !important; top: var(--neo-mobile-sidebar-height, 0px) !important; padding: .85rem 1rem !important; }
          .neo-admin-topbar > div {
            align-items: flex-start !important;
            gap: .75rem !important;
            flex-direction: column !important;
          }
          .neo-admin-topbar > div > div:last-child {
            width: 100% !important;
            justify-content: space-between !important;
          }
          .neo-admin-content {
            width: 100% !important;
            min-width: 0 !important;
            padding: 1rem !important;
            overflow-x: hidden !important;
          }
          .neo-admin-content h1 { font-size: clamp(1.65rem, 8vw, 2.25rem) !important; line-height: 1.05 !important; }
          .neo-admin-content h2 { font-size: clamp(1.35rem, 6vw, 1.8rem) !important; line-height: 1.1 !important; }
          .neo-admin-content .grid { grid-template-columns: 1fr !important; }
          .neo-admin-content .grid > [class*="col-span-"] { grid-column: 1 / -1 !important; }
          .neo-admin-content .flex.items-center.justify-between,
          .neo-admin-content .flex.items-end.justify-between {
            align-items: stretch !important;
            flex-direction: column !important;
            gap: .75rem !important;
          }
          .neo-admin-content input,
          .neo-admin-content select,
          .neo-admin-content textarea {
            max-width: 100% !important;
          }
          .neo-admin-content button { min-height: 40px; }
          .neo-admin-content .overflow-x-auto,
          .neo-admin-content .neo-mobile-table-scroll {
            width: 100% !important;
            max-width: calc(100vw - 2rem) !important;
            overflow-x: auto !important;
            overflow-y: visible !important;
            -webkit-overflow-scrolling: touch !important;
          }
          .neo-admin-content table {
            min-width: 760px !important;
            width: max-content !important;
          }
          .neo-admin-content th,
          .neo-admin-content td {
            white-space: nowrap !important;
          }
          .neo-admin-content .rounded-\[2rem\],
          .neo-admin-content .rounded-\[1\.5rem\],
          .neo-admin-content .rounded-2xl {
            border-radius: 1.15rem !important;
          }
          .neo-admin-content .text-5xl { font-size: 2.15rem !important; line-height: 1.05 !important; }
          .neo-admin-content .text-4xl { font-size: 1.9rem !important; }
          .neo-admin-content .text-3xl { font-size: 1.55rem !important; }
          .neo-admin-content .p-6 { padding: 1rem !important; }
          .neo-admin-content .p-5 { padding: 1rem !important; }
          .neo-admin-content .p-4 { padding: .85rem !important; }
          .neo-admin-content [class*="w-64"] { width: 100% !important; }
          .neo-admin-content [class*="max-w-"] { max-width: 100% !important; }
          .neo-admin-content .fixed.inset-0 { padding: 1rem !important; align-items: center !important; justify-content: center !important; }
          .neo-admin-content .fixed.inset-0 > .relative,
          .neo-admin-topbar ~ .fixed.inset-0 > .relative {
            width: min(94vw, 720px) !important;
            max-height: 86svh !important;
            overflow-y: auto !important;
          }
        }

        @media (max-width: 820px) and (orientation: landscape) {
          .neo-admin-root { min-height: 100dvh !important; }
          .neo-admin-sidebar { position: relative !important; top: auto !important; }
          .neo-admin-content { padding: .85rem !important; }
          .neo-admin-sidebar nav a { padding: .58rem .85rem !important; }
        }
      `}</style>

      {!payrollWorkspace && <aside className="neo-admin-sidebar w-64 p-4 shadow-lg flex flex-col justify-between" style={{ backgroundColor: "var(--brand-surface)" }}>
        <div>
          {(logo?.darkUrl || logo?.lightUrl) && <img src={logo.darkUrl || logo.lightUrl} alt="Org logo" className="h-10 mb-2 object-contain" />}
          <div className="mb-1 text-2xl font-extrabold leading-tight">{orgName || " "}</div>
          <h2 className="text-lg font-bold mb-6" style={{ color: "var(--brand-accent)" }}>
            Neo Admin Panel
          </h2>

          <nav className="space-y-2 text-sm">
            {can("overview") && <NavLink to="/cashup/overview" className={baseLink} style={linkStyle}>Overview</NavLink>}
            {can("staff") && <NavLink to="/cashup/staff" className={baseLink} style={linkStyle}>Staff Dashboard</NavLink>}
            {can("reports") && <NavLink to="/cashup/reports" className={baseLink} style={linkStyle}>Reports</NavLink>}
            {can("managers") && <NavLink to="/cashup/managers" className={baseLink} style={linkStyle}>Management</NavLink>}
            {can("staff-list") && <NavLink to="/cashup/staff-list" className={baseLink} style={linkStyle}>Staff</NavLink>}
            {can("active") && <NavLink to="/cashup/active-staff" className={baseLink} style={linkStyle}>Active Staff</NavLink>}
            {can("schedule") && <NavLink to="/cashup/schedule-intelligence" className={baseLink} style={linkStyle}>Schedule Intelligence</NavLink>}
            {can("labor") && <NavLink to="/cashup/labor" className={baseLink} style={linkStyle}>Labor</NavLink>}
            {can("payroll") && <NavLink to="/cashup/payroll" className={baseLink} style={linkStyle}>Payroll</NavLink>}
            {can("settings") && <NavLink to="/cashup/settings" className={baseLink} style={linkStyle}>Settings</NavLink>}
            {can("billing") && <NavLink to="/cashup/billing" className={baseLink} style={linkStyle}>Billing</NavLink>}
            {can("my-tickets") && (
              <NavLink to="/cashup/my-tickets" className={baseLink} style={linkStyle}>
                <span className="flex items-center justify-between gap-3">
                  <span>My Tickets</span>
                  {ticketUnreadCount > 0 ? (
                    <span className="min-w-[24px] rounded-full bg-rose-500 px-2 py-0.5 text-center text-[11px] font-semibold text-white">
                      {ticketUnreadCount > 99 ? "99+" : ticketUnreadCount}
                    </span>
                  ) : null}
                </span>
              </NavLink>
            )}
            {can("activity") && <NavLink to="/cashup/activity" className={baseLink} style={linkStyle}>Activity Log</NavLink>}
            {can("branding") && <NavLink to="/cashup/branding" className={baseLink} style={linkStyle}>Branding</NavLink>}
          </nav>
        </div>
      </aside>}

      <main className="neo-admin-main flex-1 flex flex-col min-w-0">
        <div className="neo-admin-topbar sticky top-0 z-40 px-6 py-3 border-b border-white/10 backdrop-blur" style={{ backgroundColor: "rgba(11, 15, 25, 0.65)" }}>
          <div className="flex items-center justify-between">
            <div className="text-sm text-white/80 flex items-center gap-2 flex-wrap">
              <div>Welcome <span className="font-semibold text-white">{displayName}</span></div>
              <span className="px-2 py-0.5 rounded-full text-xs border border-white/10 text-white/70" style={{ backgroundColor: "rgba(255,255,255,0.04)" }}>
                Role: {roles.join(", ") || "user"}
              </span>
            </div>

            <div className="flex items-center gap-3">
              <span
                className="px-3 py-1 rounded-full text-xs font-semibold border border-white/10"
                style={{
                  backgroundColor: isOnline ? "rgba(34,197,94,0.18)" : "rgba(239,68,68,0.18)",
                  color: isOnline ? "rgba(187,247,208,1)" : "rgba(254,202,202,1)",
                }}
                title={isOnline ? "Online" : "Offline"}
              >
                {isOnline ? "ONLINE" : "OFFLINE"}
              </span>

              <button
                type="button"
                onClick={refreshNeo}
                disabled={isRefreshing}
                className="inline-flex min-h-[38px] items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-sm font-semibold transition hover:border-cyan-300/35 hover:bg-white/5 disabled:cursor-wait disabled:opacity-60"
                style={{ backgroundColor: "var(--brand-surface)", color: "var(--brand-text)" }}
                title="Refresh Neo and stay on this page"
                aria-label="Refresh Neo"
              >
                <span
                  aria-hidden="true"
                  className={`text-base leading-none ${isRefreshing ? "animate-spin" : ""}`}
                >
                  ↻
                </span>
                <span className="hidden sm:inline">{isRefreshing ? "Refreshing…" : "Refresh"}</span>
              </button>

              <div className="relative">
                <button
                  onClick={() => setMenuOpen((v) => !v)}
                  className="px-3 py-2 rounded-lg text-sm border border-white/10 hover:border-white/20 transition"
                  style={{ backgroundColor: "var(--brand-surface)", color: "var(--brand-text)" }}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                >
                  Menu ▾
                </button>

                {menuOpen && (
                  <div
                    className="absolute right-0 top-full mt-2 w-60 rounded-xl border border-white/10 shadow-2xl overflow-hidden"
                    style={{ backgroundColor: "var(--brand-surface)", color: "var(--brand-text)", zIndex: 1200 }}
                  >
                    <button onClick={() => { closeAllOverlays(); navigate("/cashup/profile"); }} className="w-full text-left px-4 py-3 text-sm hover:bg-white/5 transition">Profile</button>
                    <button
                      onClick={switchToMorpheus}
                      disabled={!canSwitchToMorpheus}
                      className={`w-full text-left px-4 py-3 text-sm transition ${canSwitchToMorpheus ? "hover:bg-white/5" : "opacity-50 cursor-not-allowed"}`}
                      title={canSwitchToMorpheus ? "Open Morpheus Count" : "This profile does not have Morpheus Count access"}
                    >
                      <div>Switch to Morpheus</div>
                      <div className="mt-1 text-xs opacity-70">Count layer</div>
                    </button>
                    <button onClick={() => { setMenuOpen(false); setAboutOpen(true); }} className="w-full text-left px-4 py-3 text-sm hover:bg-white/5 transition">About</button>
                    <button onClick={() => { setMenuOpen(false); setHelpSendState({ status: "idle", message: "" }); setHelpOpen(true); }} className="w-full text-left px-4 py-3 text-sm hover:bg-white/5 transition">Help / Contact</button>
                    <div className="h-px bg-white/10" />
                    <button
                      onClick={() => { setMenuOpen(false); setShowLogoutConfirm(true); }}
                      className="w-full text-left px-4 py-3 text-sm font-semibold text-red-200 transition hover:bg-red-500/10"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {showLogoutConfirm && (
          <div className="fixed inset-0 z-[1400] flex items-center justify-center px-4">
            <div className="absolute inset-0 bg-slate-950/55 backdrop-blur-md" onClick={() => setShowLogoutConfirm(false)} />
            <div
              className="relative w-full max-w-md rounded-[1.75rem] border border-cyan-300/18 p-6 shadow-[0_30px_120px_rgba(0,0,0,.55)]"
              style={{ background: "linear-gradient(180deg, rgba(8,20,40,.96), rgba(4,13,27,.98))", color: "var(--brand-text)" }}
            >
              <div className="text-xs uppercase tracking-[0.35em] text-cyan-200/85">Session control</div>
              <div className="mt-3 text-3xl font-black text-white">Log out of Neo?</div>
              <p className="mt-3 text-sm leading-7 text-slate-300">
                You will be returned to the MatrixOS login page and this Neo session will close.
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowLogoutConfirm(false)}
                  className="rounded-full border border-white/12 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/5"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={doLogout}
                  className="rounded-full bg-cyan-400 px-5 py-3 text-sm font-bold text-slate-950 transition hover:bg-cyan-300"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}

        {aboutOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/60" onClick={() => setAboutOpen(false)} />
            <div className="relative w-[min(560px,92vw)] rounded-2xl border border-white/10 shadow-2xl p-6" style={{ backgroundColor: "var(--brand-surface)", color: "var(--brand-text)" }}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-lg font-semibold">About Neo Admin Panel</div>
                  <div className="text-sm opacity-70 mt-1">MatrixOS · Workforce Intelligence</div>
                </div>
                <button onClick={() => setAboutOpen(false)} className="px-3 py-2 rounded-lg text-sm border border-white/10 hover:border-white/20 hover:bg-white/5 transition">✕</button>
              </div>
              <div className="mt-4 text-sm leading-relaxed">Neo is the workforce layer of MatrixOS, built to keep daily operations visible, accountable and responsive across every site. It connects attendance, scheduling, duties, leave, labor intelligence and operational reporting in one place.</div>
              <div className="mt-4 text-sm opacity-80">© {new Date().getFullYear()} MatrixOS. Operate with Clarity.</div>
              <div className="mt-6 flex justify-end">
                <button onClick={() => setAboutOpen(false)} className="px-4 py-2 rounded-lg text-sm border border-white/10 hover:border-white/20 hover:bg-white/5 transition">Close</button>
              </div>
            </div>
          </div>
        )}

        {helpOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/60" onClick={() => setHelpOpen(false)} />
            <div className="relative w-[min(760px,92vw)] rounded-2xl border border-white/10 shadow-2xl p-6" style={{ backgroundColor: "var(--brand-surface)", color: "var(--brand-text)" }}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-lg font-semibold">Help / Contact</div>
                  <div className="text-sm opacity-70 mt-1">Send a support request to MatrixOS</div>
                </div>
                <button onClick={() => setHelpOpen(false)} className="px-3 py-2 rounded-lg text-sm border border-white/10 hover:border-white/20 hover:bg-white/5 transition">✕</button>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs opacity-70 mb-1">Which area do you need help with?</div>
                  <div ref={helpTabRef} className="relative">
                    <button
                      type="button"
                      onClick={() => setHelpTabMenuOpen((v) => !v)}
                      className="w-full px-3 py-2 rounded-lg border border-white/10 flex items-center justify-between gap-3"
                      style={{ backgroundColor: "rgba(0,0,0,0.18)", color: "var(--text)" }}
                    >
                      <span style={{ opacity: helpTab ? 1 : 0.75 }}>{helpTabLabel}</span>
                      <span style={{ opacity: 0.7 }}>▾</span>
                    </button>

                    {helpTabMenuOpen && (
                      <div role="listbox" className="absolute left-0 right-0 mt-2 rounded-xl border border-white/10 overflow-hidden" style={{ zIndex: 30, background: "rgba(11, 15, 25, 0.98)", boxShadow: "0 18px 50px rgba(0,0,0,0.55)", maxHeight: 280, overflowY: "auto" }}>
                        {helpTabOptions.map((opt) => (
                          <button
                            key={opt.value || "__empty"}
                            type="button"
                            onClick={() => { setHelpTab(opt.value); setHelpTabMenuOpen(false); }}
                            className="w-full text-left px-3 py-2 text-sm"
                            style={{ background: opt.value === helpTab ? "rgba(0, 225, 167, 0.12)" : "transparent", color: "var(--text)", border: "none", cursor: "pointer" }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = opt.value === helpTab ? "rgba(0, 225, 167, 0.16)" : "rgba(255,255,255,0.06)"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = opt.value === helpTab ? "rgba(0, 225, 167, 0.12)" : "transparent"; }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <div className="text-xs opacity-70 mb-1">Short topic</div>
                  <input value={helpTopic} onChange={(e) => setHelpTopic(e.target.value)} placeholder="e.g. Payroll totals not matching" className="w-full px-3 py-2 rounded-lg border border-white/10 bg-transparent" />
                </div>
              </div>

              <div className="mt-4">
                <div className="text-xs opacity-70 mb-1">What do you need help with?</div>
                <textarea value={helpMessage} onChange={(e) => setHelpMessage(e.target.value)} rows={6} placeholder="Tell us what you expected vs what happened. If you can, include steps to reproduce." className="w-full px-3 py-2 rounded-lg border border-white/10 bg-transparent resize-none" />
                <div className="text-xs opacity-60 mt-2">Tip: The email will automatically include your org, user, page, and time.</div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
                <button onClick={() => { setHelpTab(""); setHelpTopic(""); setHelpMessage(""); setHelpSendState({ status: "idle", message: "" }); }} className="px-4 py-2 rounded-lg text-sm border border-white/10 hover:border-white/20 hover:bg-white/5 transition">Clear</button>
                <button
                  onClick={openMailToSupport}
                  disabled={!canSendHelp || helpSendState.status === "sending"}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold ${canSendHelp && helpSendState.status !== "sending" ? "" : "opacity-50 cursor-not-allowed"}`}
                  style={{ backgroundColor: canSendHelp && helpSendState.status !== "sending" ? "var(--brand-accent)" : "rgba(255,255,255,0.10)", color: canSendHelp && helpSendState.status !== "sending" ? "#0b0f19" : "var(--text)" }}
                >
                  {helpSendState.status === "sending" ? "Opening…" : "Send Email"}
                </button>
                <button onClick={() => setHelpOpen(false)} className="px-4 py-2 rounded-lg text-sm border border-white/10 hover:border-white/20 hover:bg-white/5 transition">Close</button>
              </div>
              {helpSendState.message ? <div className={`mt-3 text-xs ${helpSendState.status === "error" ? "text-red-300" : "text-emerald-200"}`}>{helpSendState.message}</div> : null}
              <div className="mt-4 text-xs opacity-70">Or contact: <span className="font-medium">{SUPPORT_EMAIL}</span></div>
            </div>
          </div>
        )}

        <div className={`neo-admin-content flex-1 overflow-auto ${payrollWorkspace ? "p-0" : "p-6"}`}>
          <Routes>
            <Route index element={<Navigate to={`/cashup/${firstTab}`} replace />} />
            {can("overview") && <Route path="overview" element={<OverviewTab siteScope={neoSiteScope} />} />}
            {can("staff") && <Route path="staff" element={<StaffDashboardTab siteScope={neoSiteScope} />} />}
            {can("reports") && <Route path="reports" element={<AdminReportsPage sectionName="Cash Up" siteScope={neoSiteScope} />} />}
            {can("managers") && <Route path="managers" element={<ManagersTab delegation={{ isOwner, ...managementAccess, siteScope: neoSiteScope }} />} />}
            {can("staff-list") && <Route path="staff-list" element={<StaffTab siteScope={neoSiteScope} />} />}
            {can("active") && <Route path="active-staff" element={<ActiveStaffTab siteScope={neoSiteScope} />} />}
            {can("schedule") && <Route path="schedule-intelligence" element={<ScheduleIntelligenceTab siteScope={neoSiteScope} canViewAttendancePhotos={isOwner || (hasManagementRoleAccess && managementAccess.viewAttendancePhotos === true)} />} />}
            {can("labor") && <Route path="labor" element={<LaborTab siteScope={neoSiteScope} />} />}
            {can("payroll") && <Route path="payroll" element={<PayrollTab siteScope={neoSiteScope} backPath={can("overview") ? "/cashup/overview" : "/cashup/profile"} />} />}
            {can("settings") && <Route path="settings" element={<SettingsTab siteScope={neoSiteScope} />} />}
            {can("billing") && <Route path="billing" element={<BillingTab />} />}
            {can("my-tickets") && <Route path="my-tickets" element={<MyTicketsTab />} />}
            {can("branding") && <Route path="branding" element={<BrandingTab orgId={orgId} />} />}
            {can("activity") && <Route path="activity" element={<ActivityLogPage siteScope={neoSiteScope} />} />}
            <Route path="profile" element={<ProfilePage />} />
            <Route path="*" element={<Navigate to={`/cashup/${firstTab}`} replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
